// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement, ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'

import styles from './LoraPage.module.css'
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks'
import { useAuth } from '../../../store/hooks/useAuth'
import {
    selectLorasState,
    selectLora,
    fetchIdentityLoras,
} from '../../../store/slices/lorasSlice'
import {
    selectGenerationParams,
    setPromptStrength,
    setNumOutputs,
    setNumInferenceSteps,
    setGuidanceScale,
    resetDefaults,
} from '../../../store/slices/generationSlice'
import { useLoraSwap } from '../hooks/useLoraSwap'
import {
    UserRoundPlus,
    Loader2,
    Wand2,
    Info,
    AlertTriangle,
    Globe,
    Lock,
    Users,
    Trash2,
} from 'lucide-react'
import { useBackstageNav } from '../navigator/Router'
import PrimaryButton from '../../PrimaryButton/PrimaryButton'
import { useReadOnlyMode } from '../../../store/hooks/useReadOnlyMode'
import type { LoraResponse } from 'models-ts/lora'
import { LoraShareModal } from '../components/LoraShareModal'
import { deleteLora } from '../../../services/loras'
import { describeTrainingFailure } from '../utils/trainingFailure'
import { Tooltip } from '../../shared/Tooltip/Tooltip'
import { selectActiveHasMaskStrokes } from '../../../store/selectors/selectionSelectors'

const READONLY_SHAKE_EVENT = 'readonly:shake'
const ERROR_MESSAGE = 'Failed to load identities'

const TRAINING_POLL_MS = 5000
const DELETE_CONFIRM_TIMEOUT_MS = 4000

function useInitialDataFetch(
    loading: boolean,
    refreshing: boolean
): void {
    const dispatch = useAppDispatch()
    const hasFetchedRef = useRef(false)

    useEffect(() => {
        if (hasFetchedRef.current || loading || refreshing) {
            return
        }

        hasFetchedRef.current = true
        dispatch(fetchIdentityLoras())
    }, [dispatch, loading, refreshing])
}

/**
 * Refresh while any training is in flight. Without this the list is fetched
 * exactly once on mount, so a finished training only appears after a manual
 * reload. Polling stops as soon as nothing is pending.
 */
function useTrainingPoll(items: LoraResponse[]): void {
    const dispatch = useAppDispatch()
    const hasTrainingInFlight = items.some(
        (lora) => getLoraCardState(lora) === 'training'
    )

    useEffect(() => {
        if (!hasTrainingInFlight) {
            return
        }

        const id = window.setInterval(() => {
            dispatch(fetchIdentityLoras())
        }, TRAINING_POLL_MS)

        return () => window.clearInterval(id)
    }, [dispatch, hasTrainingInFlight])
}

type LoraCardState = 'ready' | 'training' | 'failed'

/**
 * A LoRA is only "training" while its job is actually in flight. A missing
 * thumbnail does NOT mean training — thumbnail generation is best-effort and
 * failing it still leaves a usable model (see LoraService.handle_replicate_webhook).
 */
function getLoraCardState(lora: LoraResponse): LoraCardState {
    if (lora.training_status === 'failed') {
        return 'failed'
    }
    if (
        lora.training_status === 'pending' ||
        lora.training_status === 'processing'
    ) {
        return 'training'
    }
    return 'ready'
}

function triggerReadOnlyShake(): void {
    const event = new CustomEvent(READONLY_SHAKE_EVENT, { bubbles: true })
    window.dispatchEvent(event)
}

interface FailureDetailProps {
    lora: LoraResponse
    onRetry: () => void
    onDismiss: () => void
}

/**
 * Shows why a training failed, in the page rather than a hover tooltip.
 * A tooltip is invisible on touch and easy to miss, which is how the same
 * broken photo gets uploaded again.
 */
function FailureDetail({
    lora,
    onRetry,
    onDismiss,
}: FailureDetailProps): ReactElement {
    const { t } = useTranslation('canvas')
    const { fileName, raw } = describeTrainingFailure(lora.error_message)

    return (
        <div className={styles.failureDetail} role="status">
            <div className={styles.failureHeader}>
                <AlertTriangle className={styles.failureDetailIcon} />
                <span className={styles.failureTitle}>
                    {t('backstage.failureTitle')} — {lora.name}
                </span>
            </div>

            {fileName ? (
                <>
                    <div className={styles.failureLine}>
                        {t('backstage.failureBadPhoto')}
                    </div>
                    <div className={styles.failureFile}>{fileName}</div>
                    <div className={styles.failureLine}>
                        {t('backstage.failureFixHint')}
                    </div>
                </>
            ) : (
                <div className={styles.failureLine}>
                    {t('backstage.failureGenericHint')}
                </div>
            )}

            {raw && <div className={styles.failureRaw}>{raw}</div>}

            <div className={styles.failureActions}>
                <button
                    type="button"
                    className={styles.failureButton}
                    onClick={onRetry}
                >
                    {t('backstage.failureTryAgain')}
                </button>
                <button
                    type="button"
                    className={styles.failureButtonQuiet}
                    onClick={onDismiss}
                >
                    {t('backstage.failureDismiss')}
                </button>
            </div>
        </div>
    )
}

function ErrorState({ message }: { message: string }): ReactElement {
    return (
        <div className={styles.container}>
            <div className={styles.state}>{message}</div>
        </div>
    )
}

function WarningBanner(): ReactElement {
    return (
        <div className={styles.warning}>
            <AlertTriangle className={styles.warningIcon} />
            <div className={styles.warningContent}>
                <div className={styles.warningTitle}>A note on identity use:</div>
                <div className={styles.warningText}>
                    When working with celebrity or public-figure likeness, make
                    sure you have permission. Unauthorized use may violate their
                    rights and can lead to legal action.
                </div>
            </div>
        </div>
    )
}

interface ResetButtonProps {
    onReset: () => void
}

function ResetButton({ onReset }: ResetButtonProps): ReactElement {
    return (
        <button
            className={styles.resetButton}
            onClick={onReset}
            type="button"
            title="Reset to default values"
        >
            Reset to default
        </button>
    )
}

interface AddIdentityCardProps {
    onAdd: () => void
}

function AddIdentityCard({ onAdd }: AddIdentityCardProps): ReactElement {
    return (
        <button
            type="button"
            className={styles.addCard}
            onClick={onAdd}
        >
            <div className={styles.addIconWrapper}>
                <UserRoundPlus className={styles.addIcon} />
            </div>
        </button>
    )
}

interface LoraCardProps {
    lora: LoraResponse
    isSelected: boolean
    state: LoraCardState
    currentUserId: string | null
    onClick: () => void
    onBadgeClick: () => void
    onDelete: () => void
}

function LoraCard({
    lora,
    isSelected,
    state,
    currentUserId,
    onClick,
    onBadgeClick,
    onDelete,
}: LoraCardProps): ReactElement {
    const { t } = useTranslation('canvas')
    // Two-step confirm: a browser dialog would block the whole page, and
    // deletion is irreversible enough to deserve a second tap.
    const [confirmingDelete, setConfirmingDelete] = useState(false)

    // Disarm on a timer, not just on blur: a mouse click does not focus a
    // button in every browser, so blur alone can leave the card armed
    // indefinitely and turn a later stray click into a deletion.
    useEffect(() => {
        if (!confirmingDelete) {
            return
        }
        const id = window.setTimeout(
            () => setConfirmingDelete(false),
            DELETE_CONFIRM_TIMEOUT_MS
        )
        return () => window.clearTimeout(id)
    }, [confirmingDelete])
    const badge = useMemo(() => {
        if (lora.privacy === 'public') {
            return {
                title: 'Public',
                icon: <Globe className={styles.privacyIcon} aria-hidden="true" />,
            }
        }

        const isOwner = Boolean(currentUserId && lora.user_id === currentUserId)
        if (isOwner) {
            return {
                title: 'Private (yours)',
                icon: <Lock className={styles.privacyIcon} aria-hidden="true" />,
            }
        }

        return {
            title: 'Shared with you',
            icon: <Users className={styles.privacyIcon} aria-hidden="true" />,
        }
    }, [currentUserId, lora.privacy, lora.user_id])

    const isTraining = state === 'training'
    const isFailed = state === 'failed'
    const canOpenModal = state === 'ready'

    const cardTitle = isFailed
        ? lora.error_message || t('backstage.trainingFailedHint')
        : isTraining
          ? t('backstage.trainingInProgress')
          : lora.name

    return (
        <div className={styles.loraCardWrapper}>
            <button
                className={styles.loraCard}
                data-selected={isSelected}
                data-training={isTraining}
                data-state={state}
                onClick={onClick}
                title={cardTitle}
                disabled={isTraining}
            >
                {isTraining && (
                    <div className={styles.trainingPlaceholder}>
                        <Loader2 className={styles.spinner} />
                    </div>
                )}
                {isFailed && (
                    <div className={styles.failedPlaceholder}>
                        <AlertTriangle className={styles.failedIcon} />
                        <span className={styles.failedLabel}>
                            {t('backstage.trainingFailed')}
                        </span>
                    </div>
                )}
                {state === 'ready' &&
                    (lora.thumbnail_url ? (
                        <img
                            className={styles.thumbnail}
                            src={lora.thumbnail_url}
                            alt={lora.name}
                            loading="lazy"
                        />
                    ) : (
                        /* Usable model whose thumbnail generation failed —
                           still selectable, just without a preview. */
                        <div className={styles.fallbackThumbnail} aria-hidden="true">
                            <span className={styles.fallbackInitial}>
                                {lora.name.trim().charAt(0).toUpperCase()}
                            </span>
                        </div>
                    ))}
                <div className={styles.loraName}>{lora.name}</div>
            </button>

            {isFailed ? (
                <button
                    type="button"
                    className={styles.deleteBadgeButton}
                    data-confirming={confirmingDelete}
                    title={
                        confirmingDelete
                            ? t('backstage.confirmDelete')
                            : t('backstage.deleteIdentity')
                    }
                    aria-label={
                        confirmingDelete
                            ? t('backstage.confirmDelete')
                            : t('backstage.deleteIdentity')
                    }
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        if (!confirmingDelete) {
                            setConfirmingDelete(true)
                            return
                        }
                        onDelete()
                    }}
                    onBlur={() => setConfirmingDelete(false)}
                >
                    <Trash2 className={styles.privacyIcon} aria-hidden="true" />
                </button>
            ) : (
                <button
                    type="button"
                    className={styles.privacyBadgeButton}
                    title={
                        canOpenModal ? 'View sharing' : badge.title
                    }
                    data-clickable={canOpenModal}
                    onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        if (!canOpenModal) return
                        onBadgeClick()
                    }}
                    disabled={!canOpenModal}
                    aria-label={badge.title}
                >
                    {badge.icon}
                </button>
            )}
        </div>
    )
}

interface ParameterRowProps {
    id: string
    label: string
    tooltip: string
    value: number
    min: number
    max: number
    step: number
    formatValue: (value: number) => string
    onChange: (value: number) => void
}

function ParameterRow({
    id,
    label,
    tooltip,
    value,
    min,
    max,
    step,
    formatValue,
    onChange,
}: ParameterRowProps): ReactElement {
    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        onChange(Number(event.target.value))
    }

    return (
        <div className={styles.paramRow}>
            <div className={styles.labelWithTooltip}>
                <label htmlFor={id}>{label}</label>
                <Tooltip content={tooltip}>
                    <Info className={styles.tooltipIcon} />
                </Tooltip>
            </div>
            <input
                id={id}
                type="range"
                className="slider"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={handleChange}
            />
            <span className={styles.value}>{formatValue(value)}</span>
        </div>
    )
}

interface GenerationControlsProps {
    promptStrength: number
    numOutputs: number
    numInferenceSteps: number
    guidanceScale: number
    onPromptStrengthChange: (value: number) => void
    onNumOutputsChange: (value: number) => void
    onNumInferenceStepsChange: (value: number) => void
    onGuidanceScaleChange: (value: number) => void
}

function GenerationControls({
    promptStrength,
    numOutputs,
    numInferenceSteps,
    guidanceScale,
    onPromptStrengthChange,
    onNumOutputsChange,
    onNumInferenceStepsChange,
    onGuidanceScaleChange,
}: GenerationControlsProps): ReactElement {
    return (
        <div className={styles.params}>
            <ParameterRow
                id="lora-prompt-strength"
                label="base transformation:"
                tooltip="How much the AI changes the original image. Lower = keeps the base. Higher = reimagines it."
                value={promptStrength}
                min={0}
                max={1}
                step={0.01}
                formatValue={(v) => v.toFixed(2)}
                onChange={onPromptStrengthChange}
            />
            <ParameterRow
                id="lora-num-outputs"
                label="variations:"
                tooltip="Number of different image options to generate."
                value={numOutputs}
                min={1}
                max={4}
                step={1}
                formatValue={(v) => v.toString()}
                onChange={onNumOutputsChange}
            />
            <ParameterRow
                id="lora-inference-steps"
                label="render fidelity:"
                tooltip="Controls refinement and detail. Higher fidelity = smoother, more polished results (but slower)."
                value={numInferenceSteps}
                min={1}
                max={50}
                step={1}
                formatValue={(v) => v.toString()}
                onChange={onNumInferenceStepsChange}
            />
            <ParameterRow
                id="lora-guidance-scale"
                label="creative control:"
                tooltip="How strictly the AI follows your prompt. Lower = more freeform. Higher = more on-prompt."
                value={guidanceScale}
                min={1}
                max={5}
                step={0.1}
                formatValue={(v) => v.toFixed(1)}
                onChange={onGuidanceScaleChange}
            />
        </div>
    )
}

export default function LoraPage(): ReactElement {
    const readOnly = useReadOnlyMode()
    const { user } = useAuth()
    const dispatch = useAppDispatch()
    const nav = useBackstageNav()
    const { items, selectedId, loading, refreshing, error } =
        useAppSelector(selectLorasState)
    const { t } = useTranslation('canvas')
    const gen = useAppSelector(selectGenerationParams)
    const hasMaskStrokes = useAppSelector(selectActiveHasMaskStrokes)
    const { executeSwap, isProcessing } = useLoraSwap()
    const [isShareOpen, setIsShareOpen] = useState(false)
    const [shareLoraId, setShareLoraId] = useState<string | null>(null)
    const [deleteError, setDeleteError] = useState<string | null>(null)
    const [failureDetailId, setFailureDetailId] = useState<string | null>(null)

    useInitialDataFetch(loading, refreshing)
    useTrainingPoll(items)

    const handleLoraClick = (loraId: string, state: LoraCardState): void => {
        if (state === 'failed') {
            setFailureDetailId((current) =>
                current === loraId ? null : loraId
            )
            return
        }
        if (state !== 'ready') {
            return
        }
        const newSelectedId = selectedId === loraId ? null : loraId
        dispatch(selectLora(newSelectedId))
    }

    const handleAddIdentity = (): void => {
        nav.push('training')
    }

    const handleSwap = (): void => {
        if (readOnly) {
            triggerReadOnlyShake()
            return
        }
        if (!selectedId || !hasMaskStrokes || isProcessing) {
            return
        }
        executeSwap()
        nav.replace('chat')
    }

    const canSwap = Boolean(selectedId && hasMaskStrokes && !isProcessing)
    const showErrorOnly = error !== null && items.length === 0
    const shareLora = useMemo(
        () => items.find((l) => l.id === shareLoraId) ?? null,
        [items, shareLoraId]
    )
    const failureLora = useMemo(
        () => items.find((l) => l.id === failureDetailId) ?? null,
        [items, failureDetailId]
    )

    const refreshLoras = useCallback(async (): Promise<void> => {
        await dispatch(fetchIdentityLoras())
    }, [dispatch])

    const handleDeleteLora = useCallback(
        async (loraId: string): Promise<void> => {
            if (readOnly) {
                triggerReadOnlyShake()
                return
            }
            setDeleteError(null)
            try {
                await deleteLora(loraId)
            } catch {
                setDeleteError(t('backstage.deleteError'))
                return
            }
            await refreshLoras()
        },
        [readOnly, refreshLoras, t]
    )


    if (showErrorOnly) {
        return <ErrorState message={ERROR_MESSAGE} />
    }

    return (
        <div className={styles.container}>
            <WarningBanner />

            {deleteError && <div className={styles.state}>{deleteError}</div>}

            <div className={styles.grid}>
                <AddIdentityCard onAdd={handleAddIdentity} />
                {items.map((lora) => {
                    const state = getLoraCardState(lora)
                    const isSelected = selectedId === lora.id && state === 'ready'
                    return (
                        <LoraCard
                            key={lora.id}
                            lora={lora}
                            isSelected={isSelected}
                            state={state}
                            currentUserId={user?.id ?? null}
                            onClick={() => handleLoraClick(lora.id, state)}
                            onBadgeClick={() => {
                                setShareLoraId(lora.id)
                                setIsShareOpen(true)
                            }}
                            onDelete={() => void handleDeleteLora(lora.id)}
                        />
                    )
                })}
            </div>

            {failureLora && (
                <FailureDetail
                    lora={failureLora}
                    onRetry={() => {
                        setFailureDetailId(null)
                        handleAddIdentity()
                    }}
                    onDismiss={() => setFailureDetailId(null)}
                />
            )}

            <div className={styles.controls}>
                <ResetButton
                    onReset={() => dispatch(resetDefaults())}
                />
                <GenerationControls
                    promptStrength={gen.prompt_strength}
                    numOutputs={gen.num_outputs}
                    numInferenceSteps={gen.num_inference_steps}
                    guidanceScale={gen.guidance_scale}
                    onPromptStrengthChange={(value) =>
                        dispatch(setPromptStrength(value))
                    }
                    onNumOutputsChange={(value) =>
                        dispatch(setNumOutputs(value))
                    }
                    onNumInferenceStepsChange={(value) =>
                        dispatch(setNumInferenceSteps(value))
                    }
                    onGuidanceScaleChange={(value) =>
                        dispatch(setGuidanceScale(value))
                    }
                />
                <PrimaryButton
                    onClick={handleSwap}
                    disabled={!readOnly && !canSwap}
                    loading={isProcessing}
                    icon={<Wand2 aria-hidden="true" />}
                    loadingIcon={<Loader2 aria-hidden="true" />}
                    fullWidth
                >
                    {isProcessing ? 'Processing...' : 'swap'}
                </PrimaryButton>
            </div>

            {shareLoraId && (
                <LoraShareModal
                    isOpen={isShareOpen}
                    onClose={() => setIsShareOpen(false)}
                    loraId={shareLoraId}
                    identityName={shareLora?.name ?? 'Identity'}
                    canManage={Boolean(user?.id && shareLora?.user_id === user.id)}
                    privacy={shareLora?.privacy ?? 'private'}
                    onChanged={async () => {
                        await refreshLoras()
                    }}
                />
            )}
        </div>
    )
}
